import React from "react";
import { MapPin, Clock, ArrowRight } from "lucide-react";
import { API } from "@/lib/apiClient";

const LOCATION_COORDS = {
  "green-lanes": { lat: 51.5715, lon: -0.0985 },
  "hampstead-heath": { lat: 51.5536, lon: -0.1607 },
  "finsbury-park": { lat: 51.5665, lon: -0.1170 },
};

export function locCoords(loc) {
  if (loc.lat != null && loc.lon != null && loc.lat !== "" && loc.lon !== "")
    return { lat: Number(loc.lat), lon: Number(loc.lon) };
  return LOCATION_COORDS[loc.id] || null;
}

export const photoUrl = (loc) => (loc?.photo_path ? `${API}/files/${loc.photo_path}` : null);

function osmEmbed(c) {
  const dLon = 0.011, dLat = 0.006;
  const bbox = [c.lon - dLon, c.lat - dLat, c.lon + dLon, c.lat + dLat].join("%2C");
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${c.lat}%2C${c.lon}`;
}

export function LocationMap({ loc }) {
  const c = locCoords(loc);
  if (!c) {
    return (
      <div className="relative aspect-[16/7] w-full overflow-hidden rounded-2xl bg-ink-700 flex items-center justify-center" data-testid={`location-map-${loc.id}`}>
        <div className="absolute inset-0 mesh-top opacity-50" />
        <p className="relative label text-ice/70">Location to be confirmed</p>
      </div>
    );
  }
  return (
    <div className="relative aspect-[16/9] sm:aspect-[16/7] w-full overflow-hidden rounded-2xl bg-ink-700 border border-white/8" data-testid={`location-map-${loc.id}`}>
      <iframe title={`Map of ${loc.name}`} src={osmEmbed(c)} className="absolute inset-0 h-full w-full" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address || `${loc.name} ${loc.studio || ""} London`)}`}
        target="_blank" rel="noreferrer" data-testid={`location-directions-${loc.id}`}
        className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full glass px-3.5 py-1.5 text-xs text-ice hover:text-cyan transition-colors">
        <MapPin size={13} /> Directions
      </a>
    </div>
  );
}

export function Timetable({ loc, onBook, availability = {} }) {
  if (loc.external) {
    return (
      <div className="p-6 text-center" data-testid={`external-${loc.id}`}>
        <p className="text-ice/70">Classes at this studio are booked directly through the venue.</p>
        {loc.external_url && (
          <a href={loc.external_url} target="_blank" rel="noreferrer" data-testid={`external-book-${loc.id}`}
            className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-white bg-electric rounded-full px-5 py-2.5 hover:bg-[#1a5cff] transition-colors">
            Book with the studio <ArrowRight size={14} />
          </a>
        )}
      </div>
    );
  }
  if (!loc.timetable?.length) {
    return (
      <div className="p-6 text-center" data-testid={`timetable-empty-${loc.id}`}>
        <p className="text-ice/70">Class times are being confirmed for this location.</p>
        <p className="text-mist text-sm mt-1">Join the community below to be the first to know.</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-white/6">
      {loc.timetable.map((s) => {
        const av = availability[s.id];
        const soldOut = av && av.every((d) => d.sold_out);
        const low = av && !soldOut && av.find((d) => !d.sold_out && d.remaining != null && d.remaining <= 3);
        return (
        <div key={s.id} data-testid={`timetable-slot-${s.id}`} className="flex items-center gap-3 sm:gap-4 p-4 sm:p-5 hover:bg-ink-700/40 transition-colors">
          <div className="w-16 sm:w-24 shrink-0">
            <p className="text-white font-semibold text-sm sm:text-base">{s.day}</p>
            <p className="text-cyan text-xs sm:text-sm flex items-center gap-1"><Clock size={12} /> {s.time}</p>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-base sm:text-lg text-white truncate">{s.class_name}</p>
            {s.description && <p className="text-mist text-xs sm:text-sm line-clamp-2">{s.description}</p>}
            {s.dates?.length > 0 && <p className="text-cyan/70 text-xs mt-0.5">{s.dates.join(" · ")}</p>}
            {low && <p className="text-amber-300 text-xs mt-0.5" data-testid={`slot-low-${s.id}`}>Only {low.remaining} space{low.remaining === 1 ? "" : "s"} left</p>}
          </div>
          <div className="text-right shrink-0">
            <p className="text-white font-semibold text-sm sm:text-base mb-1">£{s.price}</p>
            {soldOut ? (
              <span data-testid={`sold-out-${s.id}`} className="inline-flex items-center text-xs font-semibold text-red-200 bg-red-500/15 border border-red-400/30 rounded-full px-3 sm:px-4 py-1.5 sm:py-2">Sold out</span>
            ) : (
              <button data-testid={`book-slot-${s.id}`} onClick={() => onBook(s, loc)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-electric rounded-full px-3 sm:px-4 py-1.5 sm:py-2 hover:bg-[#1a5cff] transition-colors">
                Book <ArrowRight size={13} />
              </button>
            )}
          </div>
        </div>
        );
      })}
    </div>
  );
}
